import { useCallback, useEffect, useState } from 'react'
import apiClient from '../api/client'
import { useAuth } from '../auth/useAuth'
import ErrorAlert from '../components/ErrorAlert'
import LoadingSpinner from '../components/LoadingSpinner'

interface Access {
  inbound_open: boolean
  /** Ignora la allowlist de salida: el agente puede contactar a cualquiera. */
  outbound_open: boolean
  inbound_phones: string[]
  outbound_phones: string[]
  inbound_updated_at?: string
  outbound_updated_at?: string
}

function fmtPhone(p: string) {
  const d = p.replace(/^51/, '')
  return `+51 ${d.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3')}`
}

function normalize(raw: string) {
  const d = raw.replace(/\D/g, '')
  return d.startsWith('51') && d.length === 11 ? d.slice(2) : d
}

// ── Lista editable de números ─────────────────────────────────────────────────

function PhoneList({ phones, onChange, disabled, emptyHint }: {
  phones: string[]
  onChange: (p: string[]) => void
  disabled: boolean
  emptyHint: string
}) {
  const [nuevo, setNuevo] = useState('')
  const [error, setError] = useState<string | null>(null)

  const agregar = () => {
    const n = normalize(nuevo)
    if (n.length !== 9) { setError('El número debe tener 9 dígitos.'); return }
    if (phones.includes(n)) { setError('Ese número ya está en la lista.'); return }
    onChange([...phones, n])
    setNuevo(''); setError(null)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
      {phones.length === 0 ? (
        <div style={{ fontSize: '12px', color: 'var(--c-ink-muted)', lineHeight: 1.5 }}>
          {emptyHint}
        </div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {phones.map(p => (
            <span key={p} style={{
              display: 'inline-flex', alignItems: 'center', gap: '7px',
              background: 'var(--c-surface-faint)', padding: '5px 10px',
              borderRadius: '20px', fontSize: '12px',
              fontFamily: "'IBM Plex Mono', monospace",
            }}>
              {fmtPhone(p)}
              {!disabled && (
                <button onClick={() => onChange(phones.filter(x => x !== p))}
                  style={{ all: 'unset', cursor: 'pointer', color: 'var(--c-ink-muted)',
                           fontSize: '13px', lineHeight: 1 }}
                  title="Quitar">×</button>
              )}
            </span>
          ))}
        </div>
      )}

      {!disabled && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <input
            value={nuevo}
            onChange={e => { setNuevo(e.target.value); setError(null) }}
            onKeyDown={e => { if (e.key === 'Enter') agregar() }}
            placeholder="987654321"
            className="input"
            style={{ maxWidth: '190px', fontFamily: "'IBM Plex Mono', monospace" }}
          />
          <button onClick={agregar}
            style={{ all: 'unset', cursor: 'pointer', fontSize: '12px', fontWeight: 700,
                     color: 'var(--c-info-ink)', background: 'var(--c-info-light)',
                     padding: '7px 14px', borderRadius: '8px' }}>
            + Agregar
          </button>
        </div>
      )}
      {error && <div style={{ fontSize: '11.5px', color: 'var(--c-danger-ink)' }}>{error}</div>}
    </div>
  )
}

// ── Página ────────────────────────────────────────────────────────────────────

export default function AgentAccess() {
  const { isAdmin } = useAuth()

  const [data, setData] = useState<Access | null>(null)
  const [form, setForm] = useState<Access | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [ok, setOk] = useState<string | null>(null)

  const cargar = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const r = await apiClient.get<Access>('/agent-access')
      setData(r.data); setForm(r.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar el acceso')
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { void cargar() }, [cargar])

  const guardar = async () => {
    if (!form) return
    setSaving(true); setError(null); setOk(null)
    try {
      const r = await apiClient.put<Access>('/agent-access', {
        inbound_open: form.inbound_open,
        outbound_open: form.outbound_open,
        inbound_phones: form.inbound_phones,
        outbound_phones: form.outbound_phones,
      })
      setData(r.data); setForm(r.data)
      setOk('Cambios guardados. Aplican en menos de un minuto.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar')
    } finally { setSaving(false) }
  }

  const cambios = JSON.stringify(data) !== JSON.stringify(form)

  if (loading) return <LoadingSpinner label="Cargando acceso del agente..." />
  if (error && !form) return <ErrorAlert message={error} onRetry={cargar} />
  if (!form) return null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '780px' }}>

      {/* ENTRADA */}
      <div className="card" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div>
          <div style={{ fontSize: '13.5px', fontWeight: 800 }}>Quién puede escribirle al agente</div>
          <p style={{ fontSize: '12px', color: 'var(--c-ink-muted)', marginTop: '4px', lineHeight: 1.55 }}>
            Si alguien fuera de esta lista escribe, el agente <b>no responde</b> y la
            conversación queda marcada para que un operador la atienda desde el portal.
          </p>
        </div>

        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '9px', cursor: isAdmin ? 'pointer' : 'default' }}>
          <input
            type="checkbox" checked={form.inbound_open} disabled={!isAdmin}
            onChange={e => setForm({ ...form, inbound_open: e.target.checked })}
            style={{ marginTop: '3px', flexShrink: 0 }}
          />
          <span style={{ fontSize: '12.5px' }}>
            <b>Permitir que cualquier número le escriba</b>
            <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--c-ink-muted)', marginTop: '2px', lineHeight: 1.5 }}>
              Ignora la lista de abajo y atiende a todo el que escriba. Actívalo solo
              cuando el piloto esté liberado.
            </span>
          </span>
        </label>

        {form.inbound_open && (
          <div style={{ background: 'var(--c-warn-light)', border: '1px solid var(--c-warn-border)',
                        borderRadius: '10px', padding: '11px 14px', fontSize: '12px',
                        color: 'var(--c-warn)', lineHeight: 1.5 }}>
            El agente va a atender a <b>cualquiera</b> que escriba al número, incluidos
            los contactos reales de HubSpot que lo tengan.
          </div>
        )}

        <div style={{ opacity: form.inbound_open ? 0.45 : 1 }}>
          <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em',
                        color: 'var(--c-ink-muted)', marginBottom: '8px' }}>
            NÚMEROS HABILITADOS
          </div>
          <PhoneList
            phones={form.inbound_phones}
            onChange={p => setForm({ ...form, inbound_phones: p })}
            disabled={!isAdmin || form.inbound_open}
            emptyHint="Sin números habilitados: el agente no le responde a nadie. Agrega tu número para probar."
          />
        </div>
      </div>

      {/* SALIDA */}
      <div className="card" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div>
          <div style={{ fontSize: '13.5px', fontWeight: 800 }}>A quién puede contactar el agente</div>
          <p style={{ fontSize: '12px', color: 'var(--c-ink-muted)', marginTop: '4px', lineHeight: 1.55 }}>
            Allowlist del contacto proactivo. Estar aquí <b>autoriza</b> el envío;
            no lo dispara. El envío sigue siendo manual desde <i>Contacto proactivo</i>.
          </p>
        </div>

        {/* Mismo interruptor que la entrada, y a propósito: eran dos permisos
            simétricos con dos formas distintas de administrarse, y eso obligaba
            a cargar números a mano en un lado mientras el otro se abría con un
            clic. */}
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '9px', cursor: isAdmin ? 'pointer' : 'default' }}>
          <input
            type="checkbox" checked={form.outbound_open} disabled={!isAdmin}
            onChange={e => setForm({ ...form, outbound_open: e.target.checked })}
            style={{ marginTop: '3px', flexShrink: 0 }}
          />
          <span style={{ fontSize: '12.5px' }}>
            <b>Permitir contactar a cualquier número</b>
            <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--c-ink-muted)', marginTop: '2px', lineHeight: 1.5 }}>
              Ignora la lista de abajo. El envío sigue siendo manual desde
              Contacto proactivo: esto autoriza, no dispara nada.
            </span>
          </span>
        </label>

        {form.outbound_open && (
          <div style={{ background: 'var(--c-warn-light)', border: '1px solid var(--c-warn-border)',
                        borderRadius: '10px', padding: '11px 14px', fontSize: '12px',
                        color: 'var(--c-warn)', lineHeight: 1.5 }}>
            El agente va a poder escribirle a <b>cualquier número</b>, incluidos los
            contactos reales de HubSpot. Cada envío sigue necesitando una acción
            manual de un operador.
          </div>
        )}

        <div style={{ opacity: form.outbound_open ? 0.45 : 1 }}>
          <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em',
                        color: 'var(--c-ink-muted)', marginBottom: '8px' }}>
            NÚMEROS AUTORIZADOS
          </div>
          <PhoneList
            phones={form.outbound_phones}
            onChange={p => setForm({ ...form, outbound_phones: p })}
            disabled={!isAdmin || form.outbound_open}
            emptyHint="Sin números autorizados: el agente no puede iniciar ninguna conversación."
          />
        </div>
      </div>

      <div style={{ background: 'var(--c-surface-subtle)', borderRadius: '10px', padding: '12px 15px',
                    fontSize: '11.5px', color: 'var(--c-ink-muted)', lineHeight: 1.6 }}>
        Cargar usuarios en <b>Datos de prueba</b> no habilita ninguna de las dos
        direcciones: sirve para que el agente tenga a quién contactar y para que
        reconozca al cliente por su nombre. Los permisos se dan acá.
      </div>

      {error && <ErrorAlert message={error} />}
      {ok && (
        <div style={{ background: 'var(--c-brand-light)', border: '1px solid var(--c-brand-border)',
                      borderRadius: '10px', padding: '11px 14px', fontSize: '12.5px',
                      color: 'var(--c-brand-ink)' }}>
          {ok}
        </div>
      )}

      {isAdmin ? (
        <div style={{ display: 'flex', gap: '9px' }}>
          <button onClick={() => void guardar()} disabled={saving || !cambios}
            className="btn-primary disabled:cursor-not-allowed disabled:opacity-50">
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
          {cambios && (
            <button onClick={() => setForm(data)}
              style={{ all: 'unset', cursor: 'pointer', fontSize: '12.5px',
                       color: 'var(--c-ink-muted)', padding: '8px' }}>
              Descartar
            </button>
          )}
        </div>
      ) : (
        <p style={{ fontSize: '12.5px', color: 'var(--c-ink-muted)' }}>
          Solo los Administradores pueden modificar el acceso del agente.
        </p>
      )}
    </div>
  )
}
